import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api, { getImageUrl } from '../config/api';
import PageNavbar from './PageNavbar';
import './PosStoreMenus.css';

function formatInr(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

function resolveProductImage(url) {
  if (!url) return '';
  if (url.startsWith('blob:') || url.startsWith('http://') || url.startsWith('https://')) return url;
  return getImageUrl(url);
}

const emptyProductForm = () => ({
  categoryId: '',
  name: '',
  description: '',
  image_url: '',
  use_barcode: false,
  shortcode: '',
  is_active: true,
  stock_qty: '0',
  track_stock: true,
  variants: [{ weight: '', unit_price: '' }]
});

export default function PosStoreMenus() {
  const [stores, setStores] = useState([]);
  const [storeStats, setStoreStats] = useState({});
  const [storeId, setStoreId] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [newCat, setNewCat] = useState({ name: '' });
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [productModal, setProductModal] = useState(null); // null | { mode: 'add'|'edit', product? }
  const [productForm, setProductForm] = useState(emptyProductForm());
  const [saving, setSaving] = useState(false);
  const [copyFromId, setCopyFromId] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');

  const refreshStoreStats = useCallback(async (list) => {
    const stats = {};
    await Promise.all(
      (list || []).map(async (s) => {
        try {
          const { data } = await api.get('/pos/catalog/admin', { params: { storeId: s.id } });
          const cats = data.categories || [];
          const products = cats.reduce((n, c) => n + ((c.products || []).length), 0);
          stats[s.id] = { categories: cats.length, products };
        } catch {
          stats[s.id] = { categories: 0, products: 0 };
        }
      })
    );
    setStoreStats(stats);
  }, []);

  const loadStores = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/stores/admin');
      const list = (data.stores || []).filter((s) => Number(s.is_active) === 1);
      setStores(list);
      setStoreId((prev) => prev || (list[0] ? String(list[0].id) : ''));
      refreshStoreStats(list);
    } catch (error) {
      setMsg({ type: 'error', text: error.response?.data?.error || 'Could not load stores' });
    } finally {
      setLoading(false);
    }
  }, [refreshStoreStats]);

  const loadCatalog = useCallback(async (sid) => {
    if (!sid) {
      setCategories([]);
      return;
    }
    setCatalogLoading(true);
    setMsg(null);
    try {
      const { data } = await api.get('/pos/catalog/admin', { params: { storeId: sid } });
      const cats = data.categories || [];
      setCategories(cats);
      setStoreStats((prev) => ({
        ...prev,
        [sid]: {
          categories: cats.length,
          products: cats.reduce((n, c) => n + ((c.products || []).length), 0)
        }
      }));
    } catch (error) {
      setCategories([]);
      setMsg({ type: 'error', text: error.response?.data?.error || 'Could not load store menu' });
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  useEffect(() => {
    if (storeId) loadCatalog(storeId);
  }, [storeId, loadCatalog]);

  const selectedStore = useMemo(
    () => stores.find((s) => String(s.id) === String(storeId)) || null,
    [stores, storeId]
  );

  const openAddProduct = (categoryId) => {
    setProductForm({
      ...emptyProductForm(),
      categoryId: categoryId ? String(categoryId) : categories[0] ? String(categories[0].id) : ''
    });
    setImagePreview('');
    setProductModal({ mode: 'add' });
  };

  const openEditProduct = (product, category) => {
    const variants =
      Array.isArray(product.variants) && product.variants.length
        ? product.variants.map((v) => ({
            weight: v.weight || '',
            unit_price: String(v.unit_price ?? '')
          }))
        : [
            {
              weight: product.weight || '',
              unit_price: String(product.unit_price ?? '')
            }
          ];
    setProductForm({
      categoryId: String(category.id),
      name: product.name || '',
      description: product.description || '',
      image_url: product.image_url || '',
      use_barcode: Boolean(product.shortcode),
      shortcode: product.shortcode || '',
      is_active: product.is_active !== 0,
      stock_qty: String(product.stock_qty ?? 0),
      track_stock: product.track_stock !== 0 && product.track_stock !== false,
      variants
    });
    setImagePreview(product.image_url ? resolveProductImage(product.image_url) : '');
    setProductModal({ mode: 'edit', product });
  };

  const closeProductModal = () => {
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setProductModal(null);
    setProductForm(emptyProductForm());
    setImagePreview('');
    setImageUploading(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|gif|webp)$/i.test(file.type)) {
      setMsg({ type: 'error', text: 'Use a JPG, PNG, GIF, or WebP image' });
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setMsg({ type: 'error', text: 'Image must be under 8 MB' });
      return;
    }
    const localPreview = URL.createObjectURL(file);
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(localPreview);
    setImageUploading(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const { data } = await api.post('/pos/catalog/upload-image', fd);
      setProductForm((f) => ({ ...f, image_url: data.image_url || '' }));
      setImagePreview(resolveProductImage(data.image_url));
      URL.revokeObjectURL(localPreview);
    } catch (error) {
      setMsg({ type: 'error', text: error.response?.data?.error || 'Could not upload image' });
      setImagePreview('');
      setProductForm((f) => ({ ...f, image_url: '' }));
      URL.revokeObjectURL(localPreview);
    } finally {
      setImageUploading(false);
    }
  };

  const clearProductImage = () => {
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview('');
    setProductForm((f) => ({ ...f, image_url: '' }));
  };

  const submitCategory = async (e) => {
    e.preventDefault();
    if (!storeId || !newCat.name.trim()) return;
    try {
      await api.post('/pos/catalog/categories', {
        storeId: Number(storeId),
        name: newCat.name.trim(),
        sale_mode: 'select'
      });
      setNewCat({ name: '' });
      setCategoryModalOpen(false);
      await loadCatalog(storeId);
      setMsg({ type: 'ok', text: 'Category added' });
    } catch (error) {
      setMsg({ type: 'error', text: error.response?.data?.error || 'Could not add category' });
    }
  };

  const deleteCategory = async (catId) => {
    if (!window.confirm('Delete this category and all its products for this store?')) return;
    try {
      await api.delete(`/pos/catalog/categories/${catId}`);
      await loadCatalog(storeId);
      setMsg({ type: 'ok', text: 'Category deleted' });
    } catch (error) {
      setMsg({ type: 'error', text: error.response?.data?.error || 'Could not delete category' });
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Remove this product from the store menu?')) return;
    try {
      await api.delete(`/pos/catalog/products/${productId}`);
      await loadCatalog(storeId);
    } catch (error) {
      setMsg({ type: 'error', text: error.response?.data?.error || 'Could not delete product' });
    }
  };

  const submitProduct = async (e) => {
    e.preventDefault();
    const variants = (productForm.variants || [])
      .map((v) => ({
        weight: String(v.weight || '').trim(),
        unit_price: Number(v.unit_price)
      }))
      .filter((v) => v.weight && Number.isFinite(v.unit_price) && v.unit_price >= 0);

    if (!productForm.name.trim()) {
      setMsg({ type: 'error', text: 'Product name is required' });
      return;
    }
    if (!variants.length) {
      setMsg({ type: 'error', text: 'Add at least one weight + rate' });
      return;
    }
    if (productForm.use_barcode && !/^\d{3}$/.test(String(productForm.shortcode || '').trim())) {
      setMsg({ type: 'error', text: 'Barcode items need a 3-digit shortcode' });
      return;
    }

    setSaving(true);
    setMsg(null);
    try {
      const payload = {
        categoryId: Number(productForm.categoryId),
        name: productForm.name.trim(),
        description: productForm.description.trim(),
        image_url: productForm.image_url.trim(),
        use_barcode: Boolean(productForm.use_barcode),
        shortcode: productForm.use_barcode ? productForm.shortcode : '',
        unitPrice: variants[0].unit_price,
        weight: variants[0].weight,
        variants,
        is_active: productForm.is_active ? 1 : 0,
        stock_qty: Number(productForm.stock_qty) || 0,
        track_stock: productForm.track_stock ? 1 : 0
      };
      if (productModal?.mode === 'edit' && productModal.product?.id) {
        await api.patch(`/pos/catalog/products/${productModal.product.id}`, payload);
      } else {
        await api.post('/pos/catalog/products', payload);
      }
      closeProductModal();
      await loadCatalog(storeId);
      setMsg({ type: 'ok', text: productModal?.mode === 'edit' ? 'Product updated' : 'Product added' });
    } catch (error) {
      setMsg({ type: 'error', text: error.response?.data?.error || 'Could not save product' });
    } finally {
      setSaving(false);
    }
  };

  const copyMenu = async () => {
    if (!copyFromId || !storeId) return;
    if (
      !window.confirm(
        `Replace the entire menu for ${selectedStore?.name || 'this store'} with the menu from the selected store?`
      )
    ) {
      return;
    }
    try {
      const { data } = await api.post('/pos/catalog/copy', {
        fromStoreId: Number(copyFromId),
        toStoreId: Number(storeId)
      });
      await loadCatalog(storeId);
      setMsg({
        type: 'ok',
        text: `Copied ${data.copied?.categories || 0} categories, ${data.copied?.products || 0} products`
      });
    } catch (error) {
      setMsg({ type: 'error', text: error.response?.data?.error || 'Could not copy menu' });
    }
  };

  if (loading) {
    return (
      <div className="psm-page">
        <PageNavbar />
        <main className="psm-main">
          <p className="psm-loading">Loading stores…</p>
        </main>
      </div>
    );
  }

  return (
    <div className="psm-page">
      <PageNavbar />
      <main className="psm-main psm-main--split">
        <header className="psm-header">
          <div>
            <h1>Store-wise POS menus</h1>
            <p>
              Each outlet has its own menu. Select a store on the left, then add categories and items for that store only.
            </p>
          </div>
        </header>

        {msg ? <div className={`psm-msg psm-msg--${msg.type}`}>{msg.text}</div> : null}

        <div className="psm-layout">
          <aside className="psm-store-rail" aria-label="Stores">
            <div className="psm-store-rail-head">
              <h2>Stores</h2>
              <p>Menus are separate per outlet</p>
            </div>
            <ul className="psm-store-list">
              {stores.map((s) => {
                const stats = storeStats[s.id] || { categories: 0, products: 0 };
                const active = String(s.id) === String(storeId);
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      className={`psm-store-card${active ? ' is-active' : ''}`}
                      onClick={() => {
                        setStoreId(String(s.id));
                        setCopyFromId('');
                        setMsg(null);
                      }}
                    >
                      <strong>{s.name}</strong>
                      <span className="psm-store-code">{s.code}</span>
                      <span className="psm-store-meta">
                        {stats.categories} categor{stats.categories === 1 ? 'y' : 'ies'} · {stats.products} item
                        {stats.products === 1 ? '' : 's'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {!stores.length ? <p className="psm-empty">No active stores. Create one under Users &amp; stores.</p> : null}
          </aside>

          <section className="psm-store-workspace">
            {!storeId || !selectedStore ? (
              <p className="psm-empty">Select a store to manage its menu.</p>
            ) : (
              <>
                <div className="psm-workspace-head">
                  <div>
                    <p className="psm-workspace-eyebrow">Editing menu for</p>
                    <h2>
                      {selectedStore.name} <span className="psm-workspace-code">({selectedStore.code})</span>
                    </h2>
                    <p className="psm-workspace-note">
                      Changes here apply only to this store&apos;s POS. Other stores keep their own menus.
                    </p>
                  </div>
                  <div className="psm-workspace-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setNewCat({ name: '' });
                        setCategoryModalOpen(true);
                      }}
                    >
                      + Add category
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={!categories.length}
                      onClick={() => openAddProduct()}
                      title={!categories.length ? 'Add a category first' : ''}
                    >
                      + Add item
                    </button>
                  </div>
                </div>

                <section className="psm-toolbar psm-toolbar--copy">
                  <div className="psm-copy">
                    <label>
                      Copy full menu from another store
                      <select value={copyFromId} onChange={(e) => setCopyFromId(e.target.value)}>
                        <option value="">Select source store…</option>
                        {stores
                          .filter((s) => String(s.id) !== String(storeId))
                          .map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name} ({s.code})
                            </option>
                          ))}
                      </select>
                    </label>
                    <button type="button" className="btn btn-secondary" disabled={!copyFromId} onClick={copyMenu}>
                      Copy into {selectedStore.name}
                    </button>
                  </div>
                </section>

                {catalogLoading ? <p className="psm-loading">Loading {selectedStore.name} menu…</p> : null}

                {!catalogLoading && !categories.length ? (
                  <p className="psm-empty">
                    No categories yet for <strong>{selectedStore.name}</strong>. Use <strong>+ Add category</strong> to
                    start this store&apos;s menu.
                  </p>
                ) : null}

                <div className="psm-cat-grid">
                  {categories.map((cat) => (
                    <section key={cat.id} className="psm-cat-card">
                      <header className="psm-cat-head">
                        <div>
                          <h3>{cat.name}</h3>
                          <span className="psm-badge">Tap to sell</span>
                        </div>
                        <div className="psm-cat-actions">
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => openAddProduct(cat.id)}>
                            Add item
                          </button>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => deleteCategory(cat.id)}>
                            Delete
                          </button>
                        </div>
                      </header>
                      {(cat.products || []).length ? (
                        <div className="psm-product-grid">
                          {(cat.products || []).map((p) => {
                            const variants =
                              Array.isArray(p.variants) && p.variants.length
                                ? p.variants
                                : [{ weight: p.weight, unit_price: p.unit_price }];
                            const fromPrice = Math.min(
                              ...variants.map((v) => Number(v.unit_price)).filter(Number.isFinite)
                            );
                            return (
                              <article
                                key={p.id}
                                className={`psm-product-tile${!p.is_active ? ' is-inactive' : ''}`}
                              >
                                {p.image_url ? (
                                  <img
                                    className="psm-product-tile-img"
                                    src={resolveProductImage(p.image_url)}
                                    alt=""
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="psm-product-tile-img psm-product-tile-img--empty">No image</div>
                                )}
                                <div className="psm-product-tile-body">
                                  <strong>{p.name}</strong>
                                  {p.description ? <span className="psm-desc">{p.description}</span> : null}
                                  <span className="psm-product-tile-price">
                                    {variants.length > 1 && Number.isFinite(fromPrice)
                                      ? `from ${formatInr(fromPrice)}`
                                      : formatInr(Number.isFinite(fromPrice) ? fromPrice : p.unit_price)}
                                  </span>
                                  <span className="psm-variants">
                                    {variants.map((v, i) => (
                                      <span key={`${p.id}-${i}`} className="psm-variant-chip">
                                        {v.weight || '—'} · {formatInr(v.unit_price)}
                                      </span>
                                    ))}
                                    {p.shortcode ? (
                                      <span className="psm-variant-chip is-code">#{p.shortcode}</span>
                                    ) : null}
                                  </span>
                                </div>
                                <div className="psm-product-tile-actions">
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => openEditProduct(p, cat)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => deleteProduct(p.id)}
                                  >
                                    Remove
                                  </button>
                                </div>
                              </article>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="psm-product-empty">No items yet — add an item to this category.</p>
                      )}
                    </section>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      </main>

      {categoryModalOpen ? (
        <div
          className="psm-modal-overlay"
          role="presentation"
          onClick={() => {
            setCategoryModalOpen(false);
            setNewCat({ name: '' });
          }}
        >
          <div className="psm-modal psm-modal--sm" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h2>Add category</h2>
            <p className="psm-modal-lead">
              For <strong>{selectedStore?.name}</strong> only. Items in this category are tap-to-sell on POS.
            </p>
            <form onSubmit={submitCategory}>
              <label>
                Category name
                <input
                  value={newCat.name}
                  onChange={(e) => setNewCat((c) => ({ ...c, name: e.target.value }))}
                  placeholder="e.g. Veg snacks, Beverages"
                  autoFocus
                  required
                />
              </label>
              <div className="psm-modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setCategoryModalOpen(false);
                    setNewCat({ name: '' });
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save category
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {productModal ? (
        <div className="psm-modal-overlay" role="presentation" onClick={closeProductModal}>
          <div className="psm-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h2>{productModal.mode === 'edit' ? 'Edit item' : 'Add item'}</h2>
            <p className="psm-modal-lead">
              {selectedStore ? (
                <>
                  For <strong>{selectedStore.name}</strong> only. Default attributes are weight and rate.
                </>
              ) : (
                <>Default attributes are weight and rate. Add multiple rows for different sizes.</>
              )}
            </p>
            <form onSubmit={submitProduct}>
              <label>
                Category
                <select
                  value={productForm.categoryId}
                  onChange={(e) => setProductForm((f) => ({ ...f, categoryId: e.target.value }))}
                  required
                >
                  <option value="">Select…</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Item name
                <input
                  value={productForm.name}
                  onChange={(e) => setProductForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
              </label>
              <label>
                Description
                <textarea
                  rows={2}
                  value={productForm.description}
                  onChange={(e) => setProductForm((f) => ({ ...f, description: e.target.value }))}
                />
              </label>
              <div className="psm-image-field">
                <span className="psm-image-label">Product image</span>
                <div className="psm-image-row">
                  {imagePreview || productForm.image_url ? (
                    <img
                      className="psm-preview"
                      src={imagePreview || resolveProductImage(productForm.image_url)}
                      alt=""
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="psm-preview psm-preview--empty">No image</div>
                  )}
                  <div className="psm-image-actions">
                    <label className="btn btn-secondary btn-sm psm-upload-btn">
                      {imageUploading ? 'Uploading…' : productForm.image_url ? 'Change image' : 'Upload image'}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleImageUpload}
                        disabled={imageUploading || saving}
                        hidden
                      />
                    </label>
                    {productForm.image_url || imagePreview ? (
                      <button type="button" className="btn btn-secondary btn-sm" onClick={clearProductImage} disabled={imageUploading}>
                        Remove
                      </button>
                    ) : null}
                    <small className="psm-image-hint">JPG, PNG, GIF or WebP · max 8 MB</small>
                  </div>
                </div>
              </div>

              <fieldset className="psm-variants-fieldset">
                <legend>Weight &amp; rate</legend>
                {productForm.variants.map((v, idx) => (
                  <div key={idx} className="psm-variant-row">
                    <label>
                      Weight
                      <input
                        value={v.weight}
                        onChange={(e) =>
                          setProductForm((f) => {
                            const next = [...f.variants];
                            next[idx] = { ...next[idx], weight: e.target.value };
                            return { ...f, variants: next };
                          })
                        }
                        placeholder="e.g. 500 g / 1 kg"
                        required
                      />
                    </label>
                    <label>
                      Rate (₹)
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={v.unit_price}
                        onChange={(e) =>
                          setProductForm((f) => {
                            const next = [...f.variants];
                            next[idx] = { ...next[idx], unit_price: e.target.value };
                            return { ...f, variants: next };
                          })
                        }
                        required
                      />
                    </label>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={productForm.variants.length <= 1}
                      onClick={() =>
                        setProductForm((f) => ({
                          ...f,
                          variants: f.variants.filter((_, i) => i !== idx)
                        }))
                      }
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() =>
                    setProductForm((f) => ({
                      ...f,
                      variants: [...f.variants, { weight: '', unit_price: '' }]
                    }))
                  }
                >
                  + Add weight / rate
                </button>
              </fieldset>

              <fieldset className="psm-variants-fieldset">
                <legend>Barcode / short code (optional)</legend>
                <label className="psm-check">
                  <input
                    type="checkbox"
                    checked={Boolean(productForm.use_barcode)}
                    onChange={(e) =>
                      setProductForm((f) => ({
                        ...f,
                        use_barcode: e.target.checked,
                        shortcode: e.target.checked ? f.shortcode : ''
                      }))
                    }
                  />
                  Also sell by barcode / short code
                </label>
                {productForm.use_barcode ? (
                  <label>
                    Short code (3 digits)
                    <input
                      value={productForm.shortcode}
                      onChange={(e) =>
                        setProductForm((f) => ({
                          ...f,
                          shortcode: e.target.value.replace(/\D/g, '').slice(0, 3)
                        }))
                      }
                      maxLength={3}
                      placeholder="001"
                      required
                    />
                  </label>
                ) : (
                  <p className="psm-modal-lead" style={{ marginBottom: 0 }}>
                    Leave unchecked for normal tap-to-sell only.
                  </p>
                )}
              </fieldset>

              <label className="psm-check">
                <input
                  type="checkbox"
                  checked={productForm.is_active}
                  onChange={(e) => setProductForm((f) => ({ ...f, is_active: e.target.checked }))}
                />
                Active on POS
              </label>

              <fieldset className="psm-variants-fieldset">
                <legend>Inventory</legend>
                <label className="psm-check">
                  <input
                    type="checkbox"
                    checked={Boolean(productForm.track_stock)}
                    onChange={(e) => setProductForm((f) => ({ ...f, track_stock: e.target.checked }))}
                  />
                  Track stock (hard-block when zero)
                </label>
                <label>
                  Opening / current stock
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={productForm.stock_qty}
                    onChange={(e) => setProductForm((f) => ({ ...f, stock_qty: e.target.value }))}
                  />
                </label>
              </fieldset>

              <div className="psm-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeProductModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving || imageUploading}>
                  {saving ? 'Saving…' : imageUploading ? 'Uploading image…' : 'Save item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
